// Adapted from tinykeys: https://github.com/jamiebuilds/tinykeys/blob/main/src/tinykeys.ts
import { useEffect, useRef, useId, type KeyboardEvent as ReactKeyboardEvent } from "react"

import { earlyKeyPressedAt } from "@/lib/earlyKeys"

/**
 * A single press of a keybinding sequence.
 */
export type KeyBindingPress = [mods: string[], key: string | RegExp]

/**
 * A map of keybinding strings to event handlers.
 */
export interface KeyBindingMap {
  [keybinding: string]: (event: KeyboardEvent) => void
}

/**
 * Options for sequence matching behavior.
 */
export interface KeyBindingHandlerOptions {
  /**
   * Keybinding sequences will wait this long between key presses before
   * cancelling (default: 1000).
   *
   * Note: Setting this too low will be too fast for many users.
   */
  timeout?: number
}

/**
 * Options to configure the behavior of the React keybindings subscription.
 */
export interface UseKeybindingsOptions extends KeyBindingHandlerOptions {
  /**
   * Key presses will listen on this event target (default: document).
   */
  target?: Window | Document | HTMLElement | null
  /**
   * Key presses will listen to this event (default: "keydown").
   */
  event?: "keydown" | "keyup"
  /**
   * Key presses will use a capture listener (default: false).
   */
  capture?: boolean
  /**
   * Enables or disables the listener without changing the binding map.
   */
  enabled?: boolean
  /**
   * Ignore key presses that originate from editable elements such as inputs,
   * textareas, selects, or contenteditable regions (default: true). Document
   * bindings are also ignored while a modal dialog is open, unless their
   * priority is "modal". Drawers block them too, except "global" ones, which
   * drawers block only while something in them is marked `data-unsaved-changes`.
   */
  ignoreEditableTarget?: boolean
  /** Changing the active entity cancels an in-progress sequence. */
  scope?: string
  priority?: "modal" | "focused" | "page" | "global"
}

type ParsedKeyBinding = readonly [
  id: string,
  sequence: KeyBindingPress[],
  callback: (event: KeyboardEvent) => void,
  timeout?: number,
]

/**
 * These are the modifier keys that change the meaning of keybindings.
 *
 * Note: Ignoring "AltGraph" because it is covered by the others.
 */
const KEYBINDING_MODIFIER_KEYS = ["Shift", "Meta", "Alt", "Control"]

/**
 * Keybinding sequences should timeout if individual key presses are more than
 * 1s apart by default.
 */
const DEFAULT_TIMEOUT = 1000

/**
 * Keybinding sequences should bind to this event by default.
 */
const DEFAULT_EVENT = "keydown" as const

/**
 * Platform detection code.
 * @see https://github.com/jamiebuilds/tinykeys/issues/184
 */
const PLATFORM = typeof navigator === "object" ? navigator.platform : ""
const APPLE_DEVICE = /Mac|iPod|iPhone|iPad/.test(PLATFORM)

/**
 * An alias for creating platform-specific keybinding aliases.
 */
const MOD = APPLE_DEVICE ? "Meta" : "Control"

/**
 * Meaning of `AltGraph`, from MDN:
 * - Windows: Both Alt and Ctrl keys are pressed, or AltGr key is pressed
 * - Mac: Option key pressed
 * - Linux: Level 3 Shift key (or Level 5 Shift key) pressed
 * - Android: Not supported
 * @see https://github.com/jamiebuilds/tinykeys/issues/185
 */
const ALT_GRAPH_ALIASES = PLATFORM === "Win32" ? ["Control", "Alt"] : APPLE_DEVICE ? ["Alt"] : []

/**
 * There's a bug in Chrome that causes event.getModifierState not to exist on
 * KeyboardEvents for F1/F2/etc keys.
 */
function getModifierState(event: KeyboardEvent, mod: string) {
  if (typeof event.getModifierState !== "function") {
    return false
  }

  return (
    event.getModifierState(mod) ||
    (ALT_GRAPH_ALIASES.includes(mod) && event.getModifierState("AltGraph"))
  )
}

/**
 * Parses a keybinding string into its component presses.
 *
 * grammar    = `<sequence>`
 * <sequence> = `<press> <press> <press> ...`
 * <press>    = `<key>` or `<mods>+<key>`
 * <mods>     = `<mod>+<mod>+...`
 * <key>      = `<KeyboardEvent.key>` or `<KeyboardEvent.code>` (case-insensitive)
 * <key>      = `(<regex>)` -> `/^<regex>$/` (case-sensitive)
 */
export function parseKeybinding(str: string): KeyBindingPress[] {
  return str
    .trim()
    .split(" ")
    .map((press) => {
      let mods = press.split(/\b\+/)
      let key: string | RegExp = mods.pop() as string
      const match = key.match(/^\((.+)\)$/)

      if (match) {
        key = new RegExp(`^${match[1]}$`)
      }

      mods = mods.map((mod) => (mod === "$mod" ? MOD : mod))

      return [mods, key]
    })
}

/**
 * Tells us if a single keyboard event matches a single keybinding press.
 */
function matchKeyBindingPress(event: KeyboardEvent, [mods, key]: KeyBindingPress): boolean {
  // A printed symbol may need Shift on one keyboard layout and no modifier on another.
  const symbolShift = typeof key === "string" && key === event.key && /^[^a-zA-Z0-9\s]$/.test(key)
  return !(
    (key instanceof RegExp
      ? !(key.test(event.key) || key.test(event.code))
      : key.toUpperCase() !== event.key.toUpperCase() && key !== event.code) ||
    mods.find((mod) => !getModifierState(event, mod)) ||
    KEYBINDING_MODIFIER_KEYS.find(
      (mod) =>
        !mods.includes(mod) &&
        key !== mod &&
        !(mod === "Shift" && symbolShift) &&
        getModifierState(event, mod),
    )
  )
}

type KeyboardShortcutEvent = KeyboardEvent | ReactKeyboardEvent<Element>

function getNativeKeyboardEvent(event: KeyboardShortcutEvent): KeyboardEvent {
  return "nativeEvent" in event ? event.nativeEvent : event
}

function isComposingKeyboardEvent(event: KeyboardShortcutEvent): boolean {
  return getNativeKeyboardEvent(event).isComposing || ("isComposing" in event && event.isComposing)
}

/**
 * Matches a single keybinding against an existing keyboard event.
 */
export function isKeyboardShortcut(event: KeyboardShortcutEvent, shortcut: string): boolean {
  const sequence = parseKeybinding(shortcut)
  if (sequence.length !== 1) {
    throw new Error(`isKeyboardShortcut only supports single-press shortcuts: ${shortcut}`)
  }

  if (isComposingKeyboardEvent(event)) return false

  return matchKeyBindingPress(getNativeKeyboardEvent(event), sequence[0])
}

/**
 * Creates an event listener for handling keybinding sequences.
 */
export function createKeybindingsHandler(
  getKeyBindings: () => readonly ParsedKeyBinding[],
  options: KeyBindingHandlerOptions = {},
): EventListener & { destroy: () => void } {
  const timeout = options.timeout ?? DEFAULT_TIMEOUT
  const possibleMatches = new Map<string, { remaining: KeyBindingPress[]; expiresAt: number }>()
  let timer: number | null = null

  const handler: EventListener & { destroy?: () => void } = (event) => {
    // Autocomplete option navigation and selection can fire a plain Event
    // instead of the expected KeyboardEvent.
    if (!(event instanceof KeyboardEvent)) {
      return
    }

    if (event.isComposing || event.repeat || event.defaultPrevented) {
      handler.destroy?.()
      return
    }
    const completed: Array<{ callback: (event: KeyboardEvent) => void; continuation: boolean }> = []
    // A replayed early key counts from when it was pressed.
    const now = earlyKeyPressedAt.get(event) ?? Date.now()
    for (const [id, sequence, callback, bindingTimeout] of getKeyBindings()) {
      if ((possibleMatches.get(id)?.expiresAt ?? Infinity) <= now) possibleMatches.delete(id)
      const remainingExpectedPresses = possibleMatches.get(id)?.remaining ?? sequence
      const currentExpectedPress = remainingExpectedPresses[0]
      const matches = currentExpectedPress
        ? matchKeyBindingPress(event, currentExpectedPress)
        : false

      if (!matches) {
        // Modifier keydown events shouldn't break an in-progress sequence.
        if (!getModifierState(event, event.key)) {
          possibleMatches.delete(id)
        }
        continue
      }

      if (remainingExpectedPresses.length > 1) {
        possibleMatches.set(id, {
          remaining: remainingExpectedPresses.slice(1),
          expiresAt: now + (bindingTimeout ?? timeout),
        })
        continue
      }

      completed.push({ callback, continuation: possibleMatches.has(id) })
      possibleMatches.delete(id)
    }

    const winner = completed.find((match) => match.continuation) ?? completed[0]
    if (winner) {
      possibleMatches.clear()
      winner.callback(event)
    }
    if (timer) {
      clearTimeout(timer)
    }

    if (possibleMatches.size) {
      const current = Date.now()
      timer = window.setTimeout(
        () => possibleMatches.clear(),
        Math.max(0, ...[...possibleMatches.values()].map((match) => match.expiresAt - current)),
      )
    }
  }

  handler.destroy = () => {
    possibleMatches.clear()

    if (timer) {
      clearTimeout(timer)
      timer = null
    }
  }

  return handler as EventListener & { destroy: () => void }
}

/**
 * Returns whether the event target is an editable element that should normally
 * ignore global keybindings.
 */
export function isEditableKeyboardTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) {
    return false
  }

  return !!target.closest("input, textarea, select, [contenteditable='true']")
}

interface Subscription {
  bindings: () => readonly ParsedKeyBinding[]
  capture: boolean
  priority: number
  ignoreEditable: boolean
  timeout?: number
}

// Share sequence arbitration within each event phase while preserving capture
// semantics for owners that must defer to an element's own keyboard handler.
const dispatchers = new WeakMap<
  EventTarget,
  Map<
    string,
    {
      subscriptions: Map<symbol, Subscription>
      dispose: () => void
    }
  >
>()

const GLOBAL_PRIORITY = 0
const MODAL_PRIORITY = 3
// Dialogs hold the user's decision. Drawers are reading surfaces that global
// navigation may replace, unless something inside them holds unsaved changes;
// page actions would act on the page behind them, so drawers block those.
const BLOCKING_DIALOG =
  '[aria-modal="true"]:not([data-slot="drawer-content"]), [data-slot="drawer-content"][data-unsaved-changes], [data-slot="drawer-content"] [data-unsaved-changes]'
const OPEN_DRAWER = '[aria-modal="true"][data-slot="drawer-content"]'

/** Whether an open dialog blocks global shortcuts, for bindings that also run in text fields. */
export function isGlobalShortcutBlocked(): boolean {
  return document.querySelector(BLOCKING_DIALOG) != null
}

function subscribeKeybindings(target: EventTarget, event: string, subscription: Subscription) {
  let events = dispatchers.get(target)
  if (!events) dispatchers.set(target, (events = new Map()))
  const capture = subscription.capture
  const eventKey = `${event}:${capture}`
  let dispatcher = events.get(eventKey)
  if (!dispatcher) {
    const subscriptions = new Map<symbol, Subscription>()
    let current: KeyboardEvent | null = null
    const handler = createKeybindingsHandler(() =>
      [...subscriptions.values()]
        .sort((a, b) => b.priority - a.priority)
        .filter(
          (owner) =>
            !owner.ignoreEditable ||
            !current ||
            (!isEditableKeyboardTarget(current.target) &&
              (owner.priority === MODAL_PRIORITY ||
                target !== document ||
                !document.querySelector(
                  owner.priority === GLOBAL_PRIORITY
                    ? BLOCKING_DIALOG
                    : `${BLOCKING_DIALOG}, ${OPEN_DRAWER}`,
                ))),
        )
        .flatMap((owner) =>
          owner
            .bindings()
            .map(([id, sequence, callback]) => [id, sequence, callback, owner.timeout] as const),
        ),
    )
    const reset = () => handler.destroy()
    const listener = (native: Event) => {
      current = native instanceof KeyboardEvent ? native : null
      if (current?.key === "Escape") reset()
      handler(native)
      current = null
    }
    target.addEventListener(event, listener, capture)
    window.addEventListener("blur", reset)
    document.addEventListener("focusin", reset)
    document.addEventListener("compositionstart", reset)
    dispatcher = {
      subscriptions,
      dispose: () => {
        target.removeEventListener(event, listener, capture)
        window.removeEventListener("blur", reset)
        document.removeEventListener("focusin", reset)
        document.removeEventListener("compositionstart", reset)
        reset()
      },
    }
    events.set(eventKey, dispatcher)
  }
  // Pending sequences are keyed by owner, so owners joining or leaving cannot
  // complete or cancel another owner's sequence.
  const owner = Symbol()
  dispatcher.subscriptions.set(owner, subscription)
  return () => {
    dispatcher.subscriptions.delete(owner)
    if (!dispatcher.subscriptions.size) {
      dispatcher.dispose()
      events.delete(eventKey)
    }
  }
}

/** Subscribes an owner without duplicating event listeners or retaining stale callbacks. */
export function useKeybindings(
  keyBindingMap: KeyBindingMap,
  {
    target,
    event = DEFAULT_EVENT,
    capture = false,
    timeout,
    enabled = true,
    ignoreEditableTarget = true,
    scope,
    priority = "page",
  }: UseKeybindingsOptions = {},
) {
  const keyBindingsRef = useRef<ParsedKeyBinding[]>([])
  const ownerId = useId()
  const identity = useRef(0)
  const previousKeys = useRef("")
  useEffect(() => {
    const keys = `${scope ?? ""}\n${Object.keys(keyBindingMap).join("\n")}`
    if (keys !== previousKeys.current) {
      identity.current++
      previousKeys.current = keys
    }
    keyBindingsRef.current = Object.keys(keyBindingMap).map(
      (key) =>
        [
          `${ownerId}:${identity.current}:${key}`,
          parseKeybinding(key),
          keyBindingMap[key],
        ] as const,
    )
  }, [keyBindingMap, ownerId, scope])
  useEffect(() => {
    const eventTarget = target ?? (typeof document === "undefined" ? null : document)
    if (!enabled || !eventTarget) return
    return subscribeKeybindings(eventTarget, event, {
      bindings: () => keyBindingsRef.current,
      capture,
      priority: { global: GLOBAL_PRIORITY, page: 1, focused: 2, modal: MODAL_PRIORITY }[priority],
      timeout,
      ignoreEditable: ignoreEditableTarget,
    })
  }, [capture, enabled, event, ignoreEditableTarget, target, timeout, priority])
}
